// The suite-served Books operations (BM-BE2 reads, BM-BE7 words; BM-HK1 activation). The committed
// API snapshot has no Books family, so each call is hand-written over the configured client and
// inherits its auth, App Check, envelope and error handling; none names another service or origin.
import { givmoClient } from '@givmo/api-client';
import type { OrgModuleActivationResult } from '@givmo/org-console';
import type {
  BooksAccountActivityPage,
  BooksJournalPage,
  BooksReconciliation,
  BooksScope,
  BooksStatement,
} from './books-wire';

function booksPath(orgRef: string): string {
  return `/books/${encodeURIComponent(orgRef)}`;
}

async function read(
  url: string,
  signal: AbortSignal | undefined,
  query?: Record<string, string | number>,
): Promise<unknown> {
  const { data } = await givmoClient.get({
    url,
    signal,
    throwOnError: true,
    ...(query ? { query } : {}),
  });
  return data;
}

/** The books' home: identity, period ladder, statement catalog, chart and words. Decoded by the
 *  page, which shows nothing it cannot read. */
export async function fetchBooks(orgRef: string, signal?: AbortSignal): Promise<unknown> {
  return read(booksPath(orgRef), signal);
}

/** How far a statement reaches, exactly as its catalog row's scope takes it. */
export interface StatementQuery {
  scope: BooksScope;
  period?: string;
  fiscal_year?: number;
  first_period?: string;
  last_period?: string;
}

export async function fetchBooksStatement(
  orgRef: string,
  statement: string,
  query: StatementQuery,
  signal?: AbortSignal,
): Promise<BooksStatement> {
  const params: Record<string, string | number> = {};
  for (const [name, value] of Object.entries(query)) if (value !== undefined) params[name] = value;
  return (await read(
    `${booksPath(orgRef)}/statements/${encodeURIComponent(statement)}`,
    signal,
    params,
  )) as BooksStatement;
}

/** One page of the journal; `cursor` continues the walk the previous page served, unchanged. */
export async function fetchBooksJournal(
  orgRef: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<BooksJournalPage> {
  return (await read(
    `${booksPath(orgRef)}/journal`,
    signal,
    cursor ? { cursor } : undefined,
  )) as BooksJournalPage;
}

export async function fetchBooksAccountActivity(
  orgRef: string,
  accountCode: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<BooksAccountActivityPage> {
  return (await read(
    `${booksPath(orgRef)}/accounts/${encodeURIComponent(accountCode)}/activity`,
    signal,
    cursor ? { cursor } : undefined,
  )) as BooksAccountActivityPage;
}

export async function fetchBooksReconciliation(
  orgRef: string,
  period: string,
  signal?: AbortSignal,
): Promise<BooksReconciliation> {
  return (await read(`${booksPath(orgRef)}/reconciliation`, signal, {
    period,
  })) as BooksReconciliation;
}

/** The shared panel checks the scope, module, state and boolean before accepting this answer. */
export async function activateBooks(orgRef: string): Promise<OrgModuleActivationResult> {
  const { data } = await givmoClient.post({
    url: `${booksPath(orgRef)}/activation`,
    throwOnError: true,
  });
  return data as OrgModuleActivationResult;
}
